import {
  Mark,
  markInputRule,
  markPasteRule,
  mergeAttributes,
} from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { VariableMarkView } from "./VariableMarkView";

export interface VariableOptions {
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    variable: {
      setVariable: () => ReturnType;
      toggleVariable: () => ReturnType;
      unsetVariable: () => ReturnType;
    };
  }
}

// Simplified regex patterns to handle single characters
export const variableInputRegex = /\{\{([^}]+)\}\}$/;
export const variablePasteRegex = /\{\{([^}]+)\}\}/g;

export default Mark.create<VariableOptions>({
  name: "variable",

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-variable]",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-variable": "",
        class:
          "inline-flex items-center gap-1 px-1 py-0.5 rounded border border-gray-300 bg-gray-50 font-mono text-sm text-blue-600",
      }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableMarkView);
  },

  addCommands() {
    return {
      setVariable:
        () =>
        ({ commands }) => {
          return commands.setMark(this.name);
        },
      toggleVariable:
        () =>
        ({ commands }) => {
          return commands.toggleMark(this.name);
        },
      unsetVariable:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
    };
  },

  addInputRules() {
    return [
      markInputRule({
        find: variableInputRegex,
        type: this.type,
      }),
    ];
  },

  addPasteRules() {
    return [
      markPasteRule({
        find: variablePasteRegex,
        type: this.type,
      }),
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("variableEscape"),
        props: {
          handleKeyDown: (view, event) => {
            if (event.key === " ") {
              const { state, dispatch } = view;
              const { selection } = state;
              const marks = state.doc.rangeHasMark(
                selection.from - 1,
                selection.from,
                this.type,
              );

              if (marks) {
                const prevChar = state.doc.textBetween(
                  selection.from - 2,
                  selection.from - 1,
                );

                if (prevChar === " ") {
                  dispatch(
                    state.tr
                      .removeMark(selection.from - 1, selection.from, this.type)
                      .deleteRange(selection.from - 1, selection.from),
                  );
                  return true;
                }
              }
            }
            return false;
          },
        },
      }),
    ];
  },
});
